<script setup lang="ts">
import multipleChatsGif from '@/assets/images/multiple-chats.gif';
import projectDemoGif from '@/assets/images/project-demo.gif';
// import bulkExportGif from '@/assets/images/bulk-export.gif';
import { useBureauStats } from '@/composables/use-bureau-stats.ts';
import { Icon } from '@iconify/vue';
import { onMounted } from 'vue';

// Use shared bureau stats composable
const { bureausWithIngested: bureauStats, fetchAllBureauCounts } = useBureauStats();

onMounted(() => {
   fetchAllBureauCounts();
});

// Multiple Chats feature (2x2 in the grid)
const multipleChatsFeature = {
   title: 'Multiple Chats',
   description: 'Start and manage multiple conversation threads with Luma AI simultaneously. Each chat maintains its own context, allowing you to work on different topics or questions in parallel without losing track of your conversations.',
   image: multipleChatsGif,
};

// Projects feature (2x2 in the grid)
const projectsFeature = {
   title: 'Projects',
   description: 'Organize documents into collaborative Projects. Create private collections for personal work and add documents from search results or your Saved Documents with a single click.',
   image: projectDemoGif,
};

// Bulk Export feature
const bulkExportFeature = {
   title: 'Bulk Actions & Export',
   description: 'Select multiple documents using checkboxes, then save, add to project, or export them as a ZIP file—all at once.',
   image: null,
};

// Tutorial videos - Google Drive embeds
const tutorialVideos = [
   {
      title: 'How to Report a Document',
      embedUrl: 'https://drive.google.com/file/d/16mGECd3SqvDxyTbqFjUwdjXk2gdfS9iX/preview',
   },
   {
      title: 'Overlaying Regulatory Documents with Environmental Data',
      embedUrl: 'https://drive.google.com/file/d/1gB3VfZZDq7lV16xArNoc8ks_xFcDXFxN/preview',
   },
   {
      title: 'Using Luma to Summarize Handwritten Documents',
      embedUrl: 'https://drive.google.com/file/d/1Wi66TWo2e9JIG9I2Hgg9fy8WXLZ6-0Gg/preview',
   },
   {
      title: 'Helping Improve the Project Velocity User Experience',
      embedUrl: 'https://drive.google.com/file/d/1_6Y5E1m4LHy49bT81552XvLczZNrrN2C/preview',
   },
];

function formatNumber(num: number): string {
   return num.toLocaleString();
}

function getPercentage(ingested: number, total: number): number {
   return Math.round((ingested / total) * 100);
}
</script>

<template>
   <div class="space-y-4">
      <!-- Document Ingestion Stats Section -->
      <section v-if="bureauStats.length > 0" class="bg-[#F0F9F7] rounded-2xl p-6">
         <div class="flex items-center gap-2 mb-4">
            <Icon icon="ph:files" class="size-6 text-primary" />
            <h3 class="text-xl font-semibold text-black">
               Document Ingestion by Bureau
            </h3>
         </div>

         <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div
               v-for="stat in bureauStats"
               :key="stat.code"
               class="bg-white rounded-lg px-3 py-2 shadow-sm border border-gray-100"
            >
               <div class="flex items-center justify-between mb-1">
                  <span class="font-medium text-sm text-black">{{ stat.code }}</span>
                  <span class="text-xs text-black/60">
                     <span v-if="stat.isLoading">...</span>
                     <span v-else>{{ formatNumber(stat.ingested) }} / {{ formatNumber(stat.total) }}</span>
                  </span>
               </div>

               <!-- Progress bar -->
               <div class="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                     class="absolute inset-y-0 left-0 bg-primary rounded-full transition-all duration-500"
                     :style="{ width: `${getPercentage(stat.ingested, stat.total)}%` }"
                  />
               </div>
            </div>
         </div>

         <!-- Link to detailed spreadsheet -->
         <div class="mt-4 flex items-center gap-2">
            <Icon icon="ph:table" class="size-5 text-primary" />
            <a
               href="https://nmgov.sharepoint.com/:x:/r/sites/ENV-UPDAUnifiedPlatformforDataAccess/_layouts/15/Doc.aspx?sourcedoc=%7BE677226E-FB50-4128-8A0E-AC2137957804%7D&file=NMED%20Doc%20Types%2C%20Subtypes%2C%20Facilities%20Available%20in%20Luma%20By%20Bureau.xlsx&action=default&mobileredirect=true"
               target="_blank"
               rel="noopener noreferrer"
               class="text-base text-primary hover:underline"
            >
               View detailed document breakdown by bureau
            </a>
            <Icon icon="ph:arrow-square-out" class="size-4 text-primary/60" />
         </div>
      </section>

      <!-- Latest Features Section -->
      <section class="bg-[#E8F4F1] rounded-2xl p-6">
         <div class="flex items-center gap-2 mb-4">
            <Icon icon="ph:sparkle" class="size-6 text-primary" />
            <h3 class="text-xl font-semibold text-black">
               Latest Features
            </h3>
         </div>

         <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <!-- Projects - spans 2 columns and 2 rows -->
            <div class="md:col-span-2 md:row-span-2 bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col">
               <div class="rounded-lg overflow-hidden border border-gray-200 bg-gray-50 mb-3">
                  <img
                     :src="projectsFeature.image"
                     :alt="`${projectsFeature.title} feature demonstration`"
                     class="w-full h-auto"
                  />
               </div>
               <h4 class="font-semibold text-lg text-black mb-2">
                  {{ projectsFeature.title }}
               </h4>
               <p class="text-sm text-black/70">
                  {{ projectsFeature.description }}
               </p>
            </div>

            <!-- Saved Documents -->
            <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-primary/30 transition-colors flex flex-col">
               <div class="flex-1 bg-gray-50 rounded-lg p-3 flex items-center justify-center mb-3">
                  <Icon icon="ph:star" class="size-12 text-primary" />
               </div>
               <h4 class="font-semibold text-lg text-black mb-2">
                  Saved Documents
               </h4>
               <p class="text-sm text-black/70">
                  Star documents to save them for quick access later. Your Saved Documents page shows all your bookmarked items in one place.
               </p>
            </div>

            <!-- Bulk Actions & Export -->
            <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-primary/30 transition-colors flex flex-col">
               <div class="flex-1 bg-gray-50 rounded-lg p-3 flex items-center justify-center mb-3">
                  <svg viewBox="0 0 140 60" class="w-full max-w-[140px]">
                     <!-- Document cards with checkboxes -->
                     <rect x="5" y="5" width="45" height="50" rx="3" fill="#E5E7EB" stroke="#D1D5DB" stroke-width="1" />
                     <rect x="10" y="10" width="8" height="8" rx="2" fill="#024D50" />
                     <path d="M12 14 L14 16 L17 12" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" />
                     <rect x="22" y="11" width="22" height="4" rx="1" fill="#D1D5DB" />
                     <rect x="10" y="22" width="35" height="3" rx="1" fill="#D1D5DB" />
                     <rect x="10" y="28" width="30" height="3" rx="1" fill="#D1D5DB" />

                     <rect x="10" y="38" width="8" height="8" rx="2" fill="#024D50" />
                     <path d="M12 42 L14 44 L17 40" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" />
                     <rect x="22" y="39" width="18" height="4" rx="1" fill="#D1D5DB" />
                     <rect x="22" y="46" width="24" height="3" rx="1" fill="#D1D5DB" />

                     <!-- Arrow pointing to export -->
                     <path d="M58 30 L73 30" stroke="#024D50" stroke-width="2" stroke-linecap="round" />
                     <path d="M68 25 L75 30 L68 35" stroke="#024D50" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />

                     <!-- ZIP file icon -->
                     <rect x="85" y="10" width="45" height="40" rx="4" fill="#024D50" opacity="0.15" stroke="#024D50" stroke-width="1.5" />
                     <rect x="100" y="10" width="12" height="40" fill="#024D50" opacity="0.1" />
                     <rect x="102" y="14" width="8" height="4" rx="1" fill="#024D50" opacity="0.4" />
                     <rect x="102" y="22" width="8" height="4" rx="1" fill="#024D50" opacity="0.4" />
                     <rect x="102" y="30" width="8" height="4" rx="1" fill="#024D50" opacity="0.4" />
                     <rect x="102" y="38" width="8" height="4" rx="1" fill="#024D50" opacity="0.4" />
                     <text x="107" y="52" font-size="6" fill="#024D50" font-weight="600" text-anchor="middle">.ZIP</text>
                  </svg>
               </div>
               <h4 class="font-semibold text-lg text-black mb-2">
                  {{ bulkExportFeature.title }}
               </h4>
               <p class="text-sm text-black/70">
                  {{ bulkExportFeature.description }}
               </p>
            </div>
         </div>
      </section>

      <!-- New Features Section -->
      <section class="bg-[#E0F0ED] rounded-2xl p-6">
         <div class="flex items-center gap-2 mb-4">
            <Icon icon="ph:sparkle" class="size-6 text-primary" />
            <h3 class="text-xl font-semibold text-black">
               More Features
            </h3>
         </div>

         <!-- 3x2 Grid Layout: Multiple Chats (2x2) + Stacked features (1x2) -->
         <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <!-- Multiple Chats - spans 2 columns and 2 rows -->
            <div class="md:col-span-2 md:row-span-2 bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col">
               <!-- Multiple Chats GIF -->
               <div class="rounded-lg overflow-hidden border border-gray-200 bg-gray-50 mb-3">
                  <img
                     :src="multipleChatsFeature.image"
                     alt="Multiple Chats feature demonstration"
                     class="w-full h-auto"
                  />
               </div>
               <h4 class="font-semibold text-lg text-black mb-2">
                  {{ multipleChatsFeature.title }}
               </h4>
               <p class="text-sm text-black/70">
                  {{ multipleChatsFeature.description }}
               </p>
            </div>

            <!-- Document Sorting Feature -->
            <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-primary/30 transition-colors flex flex-col">
               <!-- Sorting Graphic -->
               <div class="flex-1 bg-gray-50 rounded-lg p-3 flex items-center justify-center mb-3">
                  <svg viewBox="0 0 140 60" class="w-full max-w-[140px]">
                     <!-- Document items with sort indicators -->
                     <rect x="10" y="5" width="80" height="14" rx="3" fill="#E5E7EB" />
                     <rect x="14" y="9" width="40" height="6" rx="2" fill="#D1D5DB" />
                     <rect x="10" y="23" width="80" height="14" rx="3" fill="#E5E7EB" />
                     <rect x="14" y="27" width="50" height="6" rx="2" fill="#D1D5DB" />
                     <rect x="10" y="41" width="80" height="14" rx="3" fill="#E5E7EB" />
                     <rect x="14" y="45" width="35" height="6" rx="2" fill="#D1D5DB" />
                     <!-- Sort arrows - thicker line, smaller filled arrowheads -->
                     <polygon points="120,8 115,14 125,14" fill="#024D50" />
                     <polygon points="120,52 115,46 125,46" fill="#024D50" />
                     <line x1="120" y1="12" x2="120" y2="48" stroke="#024D50" stroke-width="3" stroke-linecap="round" />
                  </svg>
               </div>
               <h4 class="font-semibold text-lg text-black mb-2">
                  Document Sorting
               </h4>
               <p class="text-sm text-black/70">
                  Sort documents in ascending or descending order by metadata fields, including dates.
               </p>
            </div>

            <!-- Quick Metadata Filters Feature -->
            <div class="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-primary/30 transition-colors flex flex-col">
               <!-- Filter Graphic -->
               <div class="flex-1 bg-gray-50 rounded-lg p-3 flex items-center justify-center mb-3">
                  <svg viewBox="0 0 140 60" class="w-full max-w-[140px]">
                     <!-- Document card -->
                     <rect x="5" y="5" width="60" height="50" rx="4" fill="#E5E7EB" stroke="#D1D5DB" stroke-width="1" />
                     <rect x="10" y="12" width="30" height="5" rx="2" fill="#D1D5DB" />
                     <rect x="10" y="22" width="45" height="4" rx="1" fill="#D1D5DB" />
                     <rect x="10" y="30" width="40" height="4" rx="1" fill="#D1D5DB" />
                     <!-- Clickable metadata tag - less bold -->
                     <rect x="10" y="40" width="28" height="10" rx="5" fill="#024D50" opacity="0.6" />
                     <rect x="14" y="43" width="20" height="4" rx="2" fill="white" opacity="0.9" />
                     <!-- Arrow pointing to filter -->
                     <path d="M75 30 L90 30" stroke="#024D50" stroke-width="2" stroke-linecap="round" />
                     <path d="M85 25 L92 30 L85 35" stroke="#024D50" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />
                     <!-- Filter result -->
                     <rect x="100" y="10" width="35" height="18" rx="3" fill="#024D50" opacity="0.2" stroke="#024D50" stroke-width="1" />
                     <rect x="104" y="15" width="20" height="4" rx="2" fill="#024D50" opacity="0.5" />
                     <rect x="100" y="34" width="35" height="18" rx="3" fill="#024D50" opacity="0.2" stroke="#024D50" stroke-width="1" />
                     <rect x="104" y="39" width="18" height="4" rx="2" fill="#024D50" opacity="0.5" />
                  </svg>
               </div>
               <h4 class="font-semibold text-lg text-black mb-2">
                  Quick Metadata Filters
               </h4>
               <p class="text-sm text-black/70">
                  Filter directly from document cards by clicking metadata values, including source folder.
               </p>
            </div>
         </div>
      </section>

      <!-- Tutorial Videos Section -->
      <section class="bg-[#D0E7E3] rounded-2xl p-6">
         <div class="flex items-center gap-2 mb-4">
            <Icon icon="ph:video" class="size-6 text-primary" />
            <h3 class="text-xl font-semibold text-black">
               How-To Videos
            </h3>
         </div>

         <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
               v-for="video in tutorialVideos"
               :key="video.title"
               class="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100"
            >
               <!-- Video Embed -->
               <div class="aspect-video bg-gray-100">
                  <iframe
                     :src="video.embedUrl"
                     :title="video.title"
                     class="w-full h-full"
                     frameborder="0"
                     allow="autoplay; encrypted-media"
                     allowfullscreen
                  />
               </div>

               <!-- Video title -->
               <div class="p-3">
                  <h4 class="font-medium text-sm text-black line-clamp-2">
                     {{ video.title }}
                  </h4>
               </div>
            </div>
         </div>
      </section>
   </div>
</template>
